import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useWindowSize } from 'react-use';
import { v4 as uuid } from 'uuid';
import { Box, Typography } from '@mui/material';
import { getUserSettings } from '@surfline/web-common';

import AirTempGraph from 'components/AirTempGraph';
import AreaGraph from 'components/AreaGraph';
import ChanceOfScoringGraph from 'components/ChanceOfScoringGraph';
import Information from 'components/Information';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import SeasonalConditions from 'components/SeasonalConditions';
import SurfHeightGraphTravel from 'components/SurfHeightGraphTravel/SurfHeightGraphTravel';
import WindGraph from 'components/WindGraph';
import getTravelGraphData from 'utils/getTravelGraphData';
import { useAppSelector } from 'stores/hooks';

import styles from './TravelGraphs.module.scss';

const DESKTOP_WIDTH = 1024;

const errorsToSuppress = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
];

interface Props {
  conditions: {
    display: string;
    type: string;
  }[];
  zoneId: string;
  isError?: boolean;
}

const TravelGraphs: React.FC<Props> = ({ conditions, isError, zoneId }) => {
  const userSettings = useAppSelector(getUserSettings);
  const { airTemp, chanceOfScoring, wind, surf, rainfall, waterTemp } = getTravelGraphData(
    zoneId,
    userSettings?.units ?? { surfHeight: 'FT', temperature: 'F' },
  );
  const { width: windowWidth } = useWindowSize();
  const mobile = windowWidth < DESKTOP_WIDTH;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isClient, setIsClient] = useState(false);

  const seasonalSurfDataTooltip = `All seasonal surf data is based on a prominent indicator spot in the zone. Be aware that other spots in this zone may have different conditions throughout the year.`;

  const chanceOfScoringTooltip = `
  The percentage of daylight hours where the surf is:
    1. Pumping: 6 ft+ with fair or better conditions

    2. Fun: 3-6 ft with fair or better conditions

    3. Rideable: 1-3 ft with poor-to-fair or better conditions

    4. Poor: everything else
  `;

  useEffect(() => {
    setActiveIndex(0);
  }, [mobile]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <div className={classNames(styles.travelGraphs, 'sl-section-container')}>
      <div className={styles.heading}>
        <Typography className={styles.travelGraphsHeading} variant="title2">
          Seasonal Surf Data
        </Typography>
        <div className={styles.tooltip}>
          <Information tooltip={seasonalSurfDataTooltip} />
        </div>
      </div>
      {conditions ? (
        <SeasonalConditions
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          conditions={conditions}
        />
      ) : null}
      {isClient && (
        <>
          <div className={styles.travelGraphsContainer}>
            <div className={styles.travelGraphsBackground}>
              {[...Array(12)].map(() => (
                <div key={uuid()} />
              ))}
            </div>
            {surf ? (
              <ErrorBoundary
                error={isError}
                errorBoundaryId="travel-graphs-surf-height"
                errorsToSuppress={errorsToSuppress}
                render={() => (
                  <Box className={styles.errorWrapper}>
                    <ErrorMessage />
                  </Box>
                )}
              >
                <SurfHeightGraphTravel
                  data={surf}
                  mobile={mobile}
                  activeIndex={activeIndex}
                  surfHeightUnit={userSettings?.units?.surfHeight}
                />
              </ErrorBoundary>
            ) : null}
            {wind ? (
              <ErrorBoundary
                error={isError}
                errorBoundaryId="travel-graphs-wind"
                errorsToSuppress={errorsToSuppress}
                render={() => (
                  <Box className={styles.errorWrapper}>
                    <ErrorMessage />
                  </Box>
                )}
              >
                <WindGraph data={mobile ? [wind[activeIndex]] : wind} />
              </ErrorBoundary>
            ) : null}
            {airTemp ? (
              <ErrorBoundary
                error={isError}
                errorBoundaryId="travel-graphs-air-temp"
                errorsToSuppress={errorsToSuppress}
                render={() => (
                  <Box className={styles.errorWrapper}>
                    <ErrorMessage />
                  </Box>
                )}
              >
                <AirTempGraph data={mobile ? [airTemp[activeIndex]] : airTemp} mobile={mobile} />
              </ErrorBoundary>
            ) : null}
            {waterTemp ? (
              <ErrorBoundary
                error={isError}
                errorBoundaryId="travel-graphs-water-temp"
                errorsToSuppress={errorsToSuppress}
                render={() => (
                  <Box className={styles.errorWrapper}>
                    <ErrorMessage />
                  </Box>
                )}
              >
                <AreaGraph
                  activeIndex={activeIndex}
                  ariaLabel="Water Temperature"
                  data={waterTemp}
                  dataType="waterTemp"
                  enableArea
                  mobile={mobile}
                />
              </ErrorBoundary>
            ) : null}
            {rainfall ? (
              <ErrorBoundary
                error={isError}
                errorBoundaryId="travel-graphs-rainfall"
                errorsToSuppress={errorsToSuppress}
                render={() => (
                  <Box className={styles.errorWrapper}>
                    <ErrorMessage />
                  </Box>
                )}
              >
                <AreaGraph
                  activeIndex={activeIndex}
                  ariaLabel="Rainfall"
                  data={rainfall}
                  dataType="rainfall"
                  enableArea
                  mobile={mobile}
                />
              </ErrorBoundary>
            ) : null}
          </div>
          {chanceOfScoring ? (
            <ErrorBoundary
              error={isError}
              errorBoundaryId="travel-graphs-chance-of-scoring"
              errorsToSuppress={errorsToSuppress}
              render={() => (
                <Box className={styles.errorWrapper}>
                  <ErrorMessage />
                </Box>
              )}
            >
              <>
                <div className={styles.heading}>
                  <Typography className={styles.travelGraphsHeading} variant="title2">
                    Chance of Scoring
                  </Typography>
                  <div className={styles.tooltip}>
                    <Information tooltip={chanceOfScoringTooltip} />
                  </div>
                </div>
                <ChanceOfScoringGraph
                  data={mobile ? [chanceOfScoring[activeIndex]] : chanceOfScoring}
                  mobile={mobile}
                />
              </>
            </ErrorBoundary>
          ) : null}
        </>
      )}
    </div>
  );
};

export default TravelGraphs;
